import { lazy, Suspense, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  Squares2X2Icon,
  ChartBarIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  EyeIcon,
  BeakerIcon,
  MoonIcon,
  SparklesIcon,
  SunIcon,
  ClockIcon,
  BookmarkIcon,
  ArrowRightStartOnRectangleIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';

import { ThemeProvider, useTheme } from './components/ThemeProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SupportDialog } from './components/app/SupportDialog';

// Lazy load route components for code splitting
const SessionBrowser = lazy(() => import('./pages/SessionBrowser').then(m => ({ default: m.SessionBrowser })));
const SessionDetail = lazy(() => import('./pages/SessionDetail').then(m => ({ default: m.SessionDetail })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const LivePipeline = lazy(() => import('./pages/LivePipeline').then(m => ({ default: m.LivePipeline })));
const TestPage = lazy(() => import('./pages/TestPage').then(m => ({ default: m.TestPage })));
const AppPage = lazy(() => import('./pages/AppPage').then(m => ({ default: m.AppPage })));
const UserHistoryList = lazy(() => import('./pages/UserHistoryList').then(m => ({ default: m.UserHistoryList })));
const UserHistoryDetail = lazy(() => import('./pages/UserHistoryDetail').then(m => ({ default: m.UserHistoryDetail })));
const CollectionDetail = lazy(() => import('./pages/CollectionDetail').then(m => ({ default: m.CollectionDetail })));
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const TechniquesPage = lazy(() => import('./pages/TechniquesPage').then(m => ({ default: m.TechniquesPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      refetchOnWindowFocus: false,
    },
  },
});

// Navigation items
// User-facing nav (everyone). Decision wizard + their saved decision history.
const userNavItems = [
  { path: '/app', label: 'Decision', icon: SparklesIcon },
  { path: '/app/history', label: 'History', icon: ClockIcon },
  { path: '/app/techniques', label: 'Techniques', icon: BookmarkIcon },
  { path: '/app/settings', label: 'Settings', icon: Cog6ToothIcon },
];

// Admin-only nav: pipeline observability & dev tools. Hidden from regular users.
// Temporary gate until real auth (Phase 11): a localStorage flag Aaron sets on
// his own browser (`localStorage.setItem('gd_admin','1')`). Routes stay reachable
// by direct URL; this only controls nav visibility. Replace with real role check
// once auth ships.
const adminNavItems = [
  { path: '/', label: 'Sessions', icon: Squares2X2Icon },
  { path: '/live', label: 'Live Monitor', icon: EyeIcon },
  { path: '/dashboard', label: 'Dashboard', icon: ChartBarIcon },
  { path: '/test', label: 'Test', icon: BeakerIcon },
];

// Dev-mode fallback (no Supabase env): the old localStorage flag.
// With auth enabled, the role comes from the backend profile.
function isDevAdmin(): boolean {
  try {
    return localStorage.getItem('gd_admin') === '1';
  } catch {
    return false;
  }
}

/** Shared nav state: visible items, active path (longest match wins), quota. */
function useNavState() {
  const location = useLocation();
  const { isAuthEnabled, isAuthenticated, profile } = useAuth();

  const isAdmin = isAuthEnabled ? profile?.role === 'admin' : isDevAdmin();
  // Before login (auth enabled) the nav shows no app links — just the logo
  const showNav = !isAuthEnabled || isAuthenticated;
  const items = showNav ? userNavItems : [];
  const adminItems = showNav && isAdmin ? adminNavItems : [];

  const all = [...items, ...adminItems];
  const matches = all.filter(
    (i) => location.pathname === i.path ||
      (i.path !== '/' && location.pathname.startsWith(i.path + '/'))
  );
  const activePath = matches.sort((a, b) => b.path.length - a.path.length)[0]?.path;

  const remaining = profile?.credit_balance;
  const showQuota = isAuthEnabled && !profile?.unlimited && remaining !== undefined;

  return { items, adminItems, activePath, remaining, showQuota };
}

const THEME_OPTIONS = [
  { value: 'system', label: 'System theme', icon: ComputerDesktopIcon },
  { value: 'light', label: 'Light theme', icon: SunIcon },
  { value: 'dark', label: 'Dark theme', icon: MoonIcon },
] as const;

/** Compact System/Light/Dark switch for the sidebar footer. */
function ThemeSwitch() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="gd-side-theme" role="radiogroup" aria-label="Theme">
      {THEME_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={theme === opt.value}
            className={`gd-side-theme-opt ${theme === opt.value ? 'on' : ''}`}
            onClick={() => setTheme(opt.value)}
            title={opt.label}
          >
            <Icon />
          </button>
        );
      })}
    </div>
  );
}

/** Profile block for nav footers: avatar, name, email + theme + contact + sign out. */
function UserFooter({ collapsed }: { collapsed?: boolean }) {
  const { isAuthEnabled, isAuthenticated, profile, user, logout } = useAuth();
  const [supportOpen, setSupportOpen] = useState(false);
  if (isAuthEnabled && !isAuthenticated) return null;

  const email = profile?.email ?? '';
  const name = user?.name || email.split('@')[0] || 'User';
  const initial = (name[0] || '?').toUpperCase();

  return (
    <div className="gd-side-user">
      <Link to="/app/settings" className="gd-side-user-row" title="Account settings">
        {user?.avatarUrl ? (
          <img className="gd-side-ava" src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />
        ) : (
          <div className="gd-side-ava init" aria-hidden="true">{initial}</div>
        )}
        {!collapsed && (
          <span className="gd-side-user-meta">
            <b>{name}</b>
            {email && <span>{email}</span>}
          </span>
        )}
      </Link>
      {!collapsed && <ThemeSwitch />}
      <button
        type="button"
        className="gd-side-link plain"
        onClick={() => setSupportOpen(true)}
        title="Help & feedback"
      >
        <ChatBubbleLeftEllipsisIcon />
        <span>Help &amp; feedback</span>
      </button>
      {isAuthEnabled && (
        <button type="button" className="gd-side-link signout" onClick={() => logout()}>
          <ArrowRightStartOnRectangleIcon />
          <span>Sign out</span>
        </button>
      )}
      <SupportDialog isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

function Logo({ className }: { className: string }) {
  return (
    <Link to="/app" className={className} aria-label="GetDecision home">
      <svg className="mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="1" y="1" width="30" height="30" strokeWidth="2" />
        <path className="roof" d="M8 25V13.5L16 6l8 7.5V25" strokeWidth="2.2" />
        <path d="M16 6v19" strokeWidth="2.2" />
      </svg>
      <span className="wm">GetDecision</span>
    </Link>
  );
}

/** Auth screens (/login, /register, /reset-password) render without the app
 * shell: no sidebar, no mobile nav — just a slim header linking back home. */
const AUTH_PATHS = ['/login', '/register', '/reset-password'];
function useIsAuthRoute() {
  const { pathname } = useLocation();
  return AUTH_PATHS.includes(pathname);
}

function AuthHeader() {
  if (!useIsAuthRoute()) return null;
  return (
    <header className="gd-authbar">
      <a className="gd-authbar-logo" href="https://getdecision.ai" aria-label="GetDecision home">
        <svg className="mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <rect x="1" y="1" width="30" height="30" strokeWidth="2" />
          <path className="roof" d="M8 25V13.5L16 6l8 7.5V25" strokeWidth="2.2" />
          <path d="M16 6v19" strokeWidth="2.2" />
        </svg>
        <span>GetDecision</span>
      </a>
    </header>
  );
}

/** Desktop: collapsible left sidebar (hidden <=768px). */
function SideNav() {
  const isAuthRoute = useIsAuthRoute();
  const { items, adminItems, activePath, remaining, showQuota } = useNavState();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('gd_side_collapsed') === '1'; } catch { return false; }
  });
  const toggle = () => setCollapsed((c) => {
    try { localStorage.setItem('gd_side_collapsed', c ? '0' : '1'); } catch { /* ignore */ }
    return !c;
  });

  const renderItem = (item: (typeof userNavItems)[number]) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`gd-side-link ${item.path === activePath ? 'on' : ''}`}
        title={collapsed ? item.label : undefined}
      >
        <Icon />
        <span>{item.label}</span>
      </Link>
    );
  };

  if (isAuthRoute) return null;

  return (
    <aside className={`gd-side ${collapsed ? 'min' : ''}`}>
      <Logo className="gd-side-logo" />
      <nav className="gd-side-nav">
        {items.map(renderItem)}
        {adminItems.length > 0 && (
          <>
            <div className="gd-side-cap"><span>Admin</span></div>
            {adminItems.map(renderItem)}
          </>
        )}
      </nav>
      <div className="gd-side-foot">
        {showQuota && (
          <Link
            to="/app/settings"
            className={`gd-side-quota ${remaining! <= 25 ? 'low' : ''}`}
            title="Credit balance (see Settings)"
          >
            <CircleStackIcon />
            <span>credits <b>{remaining}</b></span>
          </Link>
        )}
        <UserFooter collapsed={collapsed} />
        <button
          type="button"
          className="gd-side-toggle"
          onClick={toggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronDoubleRightIcon /> : <ChevronDoubleLeftIcon />}
          <span>Collapse</span>
        </button>
      </div>
    </aside>
  );
}

/** Mobile: compact top bar (hidden >768px). */
function MobileNav() {
  const isAuthRoute = useIsAuthRoute();
  const { items, adminItems, activePath, remaining, showQuota } = useNavState();
  const all = [...items, ...adminItems];

  if (isAuthRoute) return null;

  return (
    <nav className="gd-nav">
      <div className="gd-nav-in">
        <Logo className="gd-nav-logo" />
        <div className="gd-nav-links">
          {all.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`gd-nav-link ${item.path === activePath ? 'on' : ''}`}
              >
                <Icon />
                <span>{item.label}</span>
              </Link>
            );
          })}
          {showQuota && (
            <Link to="/app/settings" className={`gd-nav-quota ${remaining! <= 25 ? 'low' : ''}`}>
              <b>{remaining}</b> cr
            </Link>
          )}
          <MobileUserActions />
        </div>
      </div>
    </nav>
  );
}

/** Mobile top bar: theme cycle + help & feedback + sign out (sidebar footer equivalents). */
function MobileUserActions() {
  const { isAuthEnabled, isAuthenticated, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [supportOpen, setSupportOpen] = useState(false);
  if (isAuthEnabled && !isAuthenticated) return null;

  const idx = THEME_OPTIONS.findIndex((o) => o.value === theme);
  const current = THEME_OPTIONS[idx] ?? THEME_OPTIONS[0];
  const next = THEME_OPTIONS[(idx + 1) % THEME_OPTIONS.length];
  const Icon = current.icon;

  return (
    <>
      <button
        type="button"
        className="gd-nav-icon"
        onClick={() => setTheme(next.value)}
        title={`Theme: ${theme}. Tap for ${next.value}`}
        aria-label={`Switch theme (current: ${theme})`}
      >
        <Icon />
      </button>
      <button
        type="button"
        className="gd-nav-icon"
        onClick={() => setSupportOpen(true)}
        title="Help & feedback"
        aria-label="Help and feedback"
      >
        <ChatBubbleLeftEllipsisIcon />
      </button>
      {isAuthEnabled && (
        <button
          type="button"
          className="gd-nav-icon"
          onClick={() => logout()}
          title="Sign out"
          aria-label="Sign out"
        >
          <ArrowRightStartOnRectangleIcon />
        </button>
      )}
      <SupportDialog isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-3">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <div className="gd-shell min-h-screen bg-background text-foreground transition-colors">
              <SideNav />
              <div className="gd-shell-main">
                <MobileNav />
                <AuthHeader />

              <main className="max-w-7xl mx-auto px-4 py-4 w-full">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route
                      path="/app"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary
                            fallback={(_error, _reset) => (
                              <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
                                <div className="max-w-md text-center space-y-4">
                                  <svg
                                    className="w-16 h-16 mx-auto text-destructive"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                  </svg>
                                  <div>
                                    <h2 className="text-lg font-semibold">Something Went Wrong</h2>
                                    <p className="text-sm text-muted-foreground mt-2">
                                      An unexpected error occurred. Please reload the page.
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => window.location.reload()}
                                    className="px-4 py-2 text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90"
                                  >
                                    Reload Page
                                  </button>
                                </div>
                              </div>
                            )}
                          >
                            <AppPage />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    {/* Auth (public) */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    {/* Settings */}
                    <Route path="/app/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                    {/* User History */}
                    <Route path="/app/techniques" element={<ProtectedRoute><TechniquesPage /></ProtectedRoute>} />
                    <Route path="/app/history" element={<ProtectedRoute><UserHistoryList /></ProtectedRoute>} />
                    <Route path="/app/history/:id" element={<ProtectedRoute><UserHistoryDetail /></ProtectedRoute>} />
                    <Route path="/app/history/collections/:collectionId" element={<ProtectedRoute><CollectionDetail /></ProtectedRoute>} />
                    {/* Admin / observability */}
                    <Route path="/" element={<ProtectedRoute adminOnly><SessionBrowser /></ProtectedRoute>} />
                    <Route path="/sessions/:id" element={<ProtectedRoute adminOnly><SessionDetail /></ProtectedRoute>} />
                    <Route path="/live" element={<ProtectedRoute adminOnly><LivePipeline /></ProtectedRoute>} />
                    <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
                    <Route path="/test" element={<ProtectedRoute adminOnly><TestPage /></ProtectedRoute>} />
                    <Route
                      path="*"
                      element={
                        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center space-y-4">
                          <h2 className="text-2xl font-semibold">Page not found</h2>
                          <p className="text-sm text-muted-foreground">
                            The page you're looking for doesn't exist.
                          </p>
                          <Link
                            to="/app"
                            className="px-4 py-2 text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90"
                          >
                            Go to app
                          </Link>
                        </div>
                      }
                    />
                  </Routes>
                </Suspense>
              </main>
              </div>
            </div>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
